import { TableCell, Tooltip } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import MoneyIcon from '@material-ui/icons/Money';
import ListAltIcon from '@material-ui/icons/ListAlt';
import React, { Fragment, useEffect } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import styled from 'styled-components';
import Box from '../../components/Box';
import BoxBody from '../../components/Box/BoxBody';
import BoxHeader from '../../components/Box/BoxHeader';
import DataDisplayField from '../../components/DataDisplay/DataDisplayField';
import GridRow from '../../components/DataDisplay/GridRow';
import DataTable from '../../components/DataTable_v2/DataTable';
import useReduxReducer from '../../hooks/useReduxReducer';
import { getTransactionDetailAction } from './actions';
import transaction from './reducers';

const FormattedDateTime = ({ dateTime }) => {
  return (
    <>
      {dateTime && (
        <>
          <FormattedDate value={dateTime} /> <FormattedTime value={dateTime} hour="numeric" minute="numeric" second="numeric" />
        </>
      )}
    </>
  );
};

const ClosedTransactionDetail = () => {
  let { id: transactionId } = useParams();
  useReduxReducer({ transaction });
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTransactionDetailAction(transactionId));
  }, []);

  const transactionData = useSelector((state) => state?.transaction?.data);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>
      <GridRow>
        <Box>
          <BoxHeader title={transactionData?.document_no} icon={<AssignmentIcon />} color={'#43a047'} />
          <BoxBody style={{ paddingBottom: '30px' }}>
            <GridRow>
              <DataColumn>
                <DataDisplayField label={<FormattedMessage id={'transaction.documentNo'} />} value={transactionData?.document_no} />
                <DataDisplayField
                  label={<FormattedMessage id={'transaction.createdOn'} />}
                  value={<FormattedDateTime dateTime={transactionData?.created_on} />}
                />
                <DataDisplayField label={<FormattedMessage id={'transaction.createdBy'} />} value={transactionData?.created_by} />
                <DataDisplayField
                  label={<FormattedMessage id={'transaction.closedOn'} />}
                  value={<FormattedDateTime dateTime={transactionData?.modified_on} />}
                />
                <DataDisplayField label={<FormattedMessage id={'transaction.closedBy'} />} value={transactionData?.modified_by} />
              </DataColumn>
              <DataColumn>
                <DataDisplayField label={<FormattedMessage id={'transaction.itemCount'} />} value={transactionData?.itemsCount || 0} />
                <DataDisplayField
                  label={<FormattedMessage id={'transaction.netAmount'} />}
                  value={
                    <FormattedNumber value={transactionData?.itemsAmount - transactionData?.taxesAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />
                  }
                />
                <DataDisplayField
                  label={<FormattedMessage id={'transaction.taxAmount'} />}
                  value={<FormattedNumber value={transactionData?.taxesAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />}
                />
                <DataDisplayField
                  label={<FormattedMessage id={'transaction.grossAmount'} />}
                  value={<FormattedNumber value={transactionData?.itemsAmount || 0} currency={'CZK'} {...{ style: 'currency' }} />}
                />
              </DataColumn>
              <DataColumn />
              <DataColumn />
            </GridRow>
          </BoxBody>
        </Box>
      </GridRow>

      <GridRow>
        <Box>
          <BoxHeader title={<FormattedMessage id={'transaction.items'} />} icon={<ListAltIcon />} color={'#6937ff'} />
          <BoxBody style={{ paddingBottom: '30px' }}>
            <TransactionLines data={transactionData?.lines} />
          </BoxBody>
        </Box>
      </GridRow>
      <GridRow>
        <Box>
          <BoxHeader title={<FormattedMessage id={'transaction.payments'} />} icon={<AccountBalanceWalletIcon />} color={'#ce1b1b'} />
          <BoxBody style={{ paddingBottom: '30px' }}>
            <PaymentLines data={transactionData?.payments} />
          </BoxBody>
        </Box>
      </GridRow>
    </div>
  );
};

export default ClosedTransactionDetail;

const DataColumn = styled.div`
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TransactionLines = ({ data = [], loading = false }) => {
  return (
    <DataTable
      loading={loading}
      data={data}
      columns={[
        {
          id: 'itemName',
          label: <FormattedMessage id={'transaction.item.name'} />,
        },
        {
          id: 'quantity',
          label: <FormattedMessage id={'transaction.item.quantity'} />,
          align: 'right',
        },
        {
          id: 'taxPercentage',
          label: <FormattedMessage id={'transaction.taxPercentage'} />,
          align: 'right',
        },
        {
          id: 'taxAmount',
          label: <FormattedMessage id={'transaction.taxAmount'} />,
          align: 'right',
        },
        {
          id: 'netAmount',
          label: <FormattedMessage id={'transaction.netAmount'} />,
          align: 'right',
        },
        {
          id: 'grossAmount',
          label: <FormattedMessage id={'transaction.grossAmount'} />,
          align: 'right',
        },
      ]}
      template={({ name, quantity, amount, taxes }) => {
        const taxObj = taxes.reduce(
          (output, tax) => {
            output.amount += tax.amount;
            output.percentage += tax.percentage;

            return output;
          },
          { amount: 0, percentage: 0 },
        );

        return (
          <>
            <TableCell>{name}</TableCell>
            <TableCell style={{ textAlign: 'right' }}>{quantity}</TableCell>
            <TableCell style={{ textAlign: 'right' }}>
              <FormattedNumber value={taxObj?.percentage / 100 || 0} {...{ style: 'percent' }} />
            </TableCell>
            <TableCell style={{ textAlign: 'right' }}>
              <FormattedNumber value={taxObj?.amount || 0} currency={'CZK'} {...{ style: 'currency' }} />
            </TableCell>
            <TableCell style={{ textAlign: 'right' }}>
              <FormattedNumber value={amount - taxObj?.amount || 0} currency={'CZK'} {...{ style: 'currency' }} />
            </TableCell>
            <TableCell style={{ textAlign: 'right' }}>
              <FormattedNumber value={amount || 0} currency={'CZK'} {...{ style: 'currency' }} />
            </TableCell>
          </>
        );
      }}
    />
  );
};

const PaymentLines = ({ data = [], loading = false }) => {
  return (
    <DataTable
      loading={loading}
      data={data}
      columns={[
        {
          id: 'paymentType',
          label: <FormattedMessage id={'transaction.item.name'} />,
        },
        {
          id: 'paymentAmount',
          label: <FormattedMessage id={'transaction.grossAmount'} />,
          align: 'right',
        },
      ]}
      template={({ name, amount }) => {
        return (
          <>
            <TableCell>
              {name === 'CARD' && <FormattedMessage id={'payment.card'} />}
              {name === 'CASH' && <FormattedMessage id={'payment.cash'} />}
            </TableCell>
            <TableCell style={{ textAlign: 'right' }}>
              <FormattedNumber value={amount || 0} currency={'CZK'} {...{ style: 'currency' }} />
            </TableCell>
          </>
        );
      }}
    />
  );
};
